import React, { useEffect, useState, useCallback, useRef } from "react";
import { toast } from "sonner";
import { Copy, Download, Check, PencilLine, Paperclip, FileDown } from "lucide-react";
import { Card, PageHeader, StatusPill, Empty, Btn, Field, inputCls, CheckboxGroup } from "../../components/dash/Kit";
import AiNotice from "../../components/dash/AiNotice";
import { api, errMessage } from "../../lib/api";

const changeAreas = ["Caption", "Design", "Image", "Tone", "Call to Action", "Information", "Date", "Entire concept", "Other"];

export default function MyContent() {
  const [items, setItems] = useState([]);
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(true);
  const [revisionFor, setRevisionFor] = useState(null);
  const [areas, setAreas] = useState([]);
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [assets, setAssets] = useState({});
  const [uploading, setUploading] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulk, setConfirmBulk] = useState(false);
  const fileInputs = useRef({});

  const loadAssets = useCallback(async (items) => {
    const entries = await Promise.all(
      items.map(async (item) => {
        try {
          const { data } = await api.get(`/content/${item.id}/assets`);
          return [item.id, data];
        } catch {
          return [item.id, []];
        }
      })
    );
    setAssets(Object.fromEntries(entries));
  }, []);

  const load = useCallback(async (m) => {
    setLoading(true);
    try {
      const { data } = await api.get("/content", { params: m ? { month: m } : {} });
      setItems(data);
      loadAssets(data);
    } finally {
      setLoading(false);
    }
  }, [loadAssets]);

  useEffect(() => {
    api.get("/content/months").then((r) => setMonths(r.data)).catch(() => {});
    load("");
  }, [load]);

  const approve = async (id) => {
    try {
      const { data } = await api.post(`/social/approve/${id}`, {});
      toast.success(data.message || "Approved.");
      load(month);
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const approveAll = async () => {
    const pending = items.filter((i) => ["AI Draft", "Needs Review", "Ready for Customer", "Customer Reviewing"].includes(i.status));
    if (pending.length === 0) {
      toast.info("Nothing left to approve.");
      return;
    }
    setBulkBusy(true);
    let ok = 0;
    for (const item of pending) {
      try {
        await api.post(`/social/approve/${item.id}`, {});
        ok += 1;
      } catch (e) {
        console.error(`Bulk approve failed for ${item.id}`, e);
      }
    }
    setBulkBusy(false);
    setConfirmBulk(false);
    toast.success(`Approved ${ok} of ${pending.length} items.`);
    load(month);
  };

  const copyCaption = async (item) => {
    await navigator.clipboard.writeText(item.caption || "");
    toast.success("Caption copied.");
  };

  const download = (item) => {
    const text = [
      `Date: ${item.post_date}`, `Platform: ${item.platform}`, `Type: ${item.content_type}`,
      "", "Caption:", item.caption || "", "", `Hashtags: ${item.hashtags || ""}`,
      `CTA: ${item.cta || ""}`, "", item.reel_concept ? `Reel concept:\n${item.reel_concept}` : "",
    ].join("\n");
    const blob = new Blob([text], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `postmint-${item.post_date || "content"}.txt`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const uploadAsset = async (item, files) => {
    const file = files?.[0];
    if (!file) return;
    if (file.size > 6 * 1024 * 1024) {
      toast.error(`${file.name} is larger than 6MB.`);
      return;
    }
    setUploading(item.id);
    try {
      const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await api.post(`/content/${item.id}/assets`, {
        filename: file.name, mime_type: file.type, size: file.size,
        data_url: dataUrl, note: "Uploaded by customer",
      });
      toast.success("File sent to PostMint.");
      loadAssets(items);
    } catch (e) {
      toast.error(errMessage(e));
    } finally {
      setUploading("");
    }
  };

  const downloadAsset = async (asset) => {
    try {
      const { data } = await api.get(`/assets/${asset.id}`);
      const a = document.createElement("a");
      a.href = data.data_url;
      a.download = data.filename;
      a.click();
    } catch (e) {
      toast.error(errMessage(e));
    }
  };

  const submitRevision = async () => {    if (description.trim().length < 5) {
      setError("Please describe the change you'd like.");
      return;
    }
    setError("");
    try {
      await api.post("/revisions", { content_item_id: revisionFor.id, areas, description });
      toast.success("Revision request submitted.");
      setRevisionFor(null);
      setAreas([]);
      setDescription("");
      load(month);
    } catch (e) {
      setError(errMessage(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="my-content-page">
      <PageHeader
        title="My Content"
        body="Review each item, request changes or approve it for publishing. You're responsible for checking accuracy before anything is published — once you approve, PostMint schedules and publishes it to your connected accounts."
        action={
          <select
            data-testid="content-month-filter"
            className={`${inputCls} sm:w-52`}
            value={month}
            onChange={(e) => {
              setMonth(e.target.value);
              load(e.target.value);
            }}
          >
            <option value="">All months</option>
            {months.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        }
      />

      <AiNotice testId="my-content-ai-notice" />

      {items.length > 0 && (
        <Card testId="bulk-approve-card">
          {confirmBulk ? (
            <div>
              <p className="font-display text-base font-semibold text-slate-900">Approve everything waiting?</p>
              <p className="mt-2 text-sm text-slate-600">
                You are responsible for reviewing content for accuracy before publication. Approved posts are scheduled
                and published to your connected accounts.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Btn onClick={approveAll} disabled={bulkBusy} testId="bulk-approve-confirm">
                  {bulkBusy ? "Approving…" : "Yes, approve all"}
                </Btn>
                <Btn variant="outline" onClick={() => setConfirmBulk(false)} testId="bulk-approve-cancel">
                  Cancel
                </Btn>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-slate-600">Happy with everything? Approve the whole batch in one go.</p>
              <Btn variant="outline" onClick={() => setConfirmBulk(true)} testId="bulk-approve-button">
                <Check className="h-4 w-4" /> Approve All
              </Btn>
            </div>
          )}
        </Card>
      )}

        <Card testId="content-loading">Loading your content…</Card>
      ) : items.length === 0 ? (
        <Empty
          testId="content-empty"
          title="No content ready yet"
          body="Once your Brand Profile and materials are in, your content month appears here for review."
        />
      ) : (
        <div className="grid gap-4 lg:grid-cols-2" data-testid="content-list">
          {items.map((item) => (
            <Card key={item.id} testId={`content-card-${item.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex flex-wrap items-center gap-2 text-xs font-semibold text-slate-500">
                  <span>{item.post_date}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5">{item.platform}</span>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5">{item.content_type}</span>
                </div>
                <StatusPill status={item.status} testId={`content-status-${item.id}`} />
              </div>

              {item.image_url && (
                <img src={item.image_url} alt={item.title || "Content preview"} loading="lazy" className="mt-4 h-44 w-full rounded-xl object-cover" />
              )}

              <p className="mt-4 font-display text-base font-semibold text-slate-900">{item.title || "Untitled"}</p>
              {item.caption && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">{item.caption}</p>}
              {item.reel_concept && (
                <p className="mt-3 whitespace-pre-line rounded-xl bg-slate-50 p-3 text-sm text-slate-600">{item.reel_concept}</p>
              )}
              {item.hashtags && <p className="mt-3 text-xs text-slate-400">{item.hashtags}</p>}
              {item.cta && <p className="mt-2 text-xs font-semibold text-slate-700">CTA: {item.cta}</p>}

              <div className="mt-4 rounded-xl border border-slate-200 p-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Post files</p>
                {(assets[item.id] || []).length === 0 ? (
                  <p className="mt-1.5 text-xs text-slate-500">
                    No files yet. You can upload your own photo or draft for this post.
                  </p>
                ) : (
                  <ul className="mt-2 space-y-1.5">
                    {(assets[item.id] || []).map((a) => (
                      <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700">
                        <span className="min-w-0 truncate">
                          {a.filename}
                          <span className="ml-2 text-xs text-slate-400">
                            {a.uploaded_by === "admin" ? "from PostMint" : "yours"}
                          </span>
                        </span>
                        <Btn variant="ghost" testId={`asset-download-${a.id}`} onClick={() => downloadAsset(a)}>
                          <FileDown className="h-4 w-4" /> Download
                        </Btn>
                      </li>
                    ))}
                  </ul>
                )}
                <input
                  ref={(el) => { fileInputs.current[item.id] = el; }}
                  type="file"
                  accept=".jpg,.jpeg,.png,.webp,.mp4,.mov,.pdf"
                  className="hidden"
                  data-testid={`asset-file-input-${item.id}`}
                  onChange={(e) => uploadAsset(item, e.target.files)}
                />
                <Btn
                  variant="outline"
                  testId={`asset-upload-button-${item.id}`}
                  className="mt-3"
                  disabled={uploading === item.id}
                  onClick={() => fileInputs.current[item.id]?.click()}
                >
                  <Paperclip className="h-4 w-4" /> {uploading === item.id ? "Uploading…" : "Upload a file"}
                </Btn>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                <Btn testId={`approve-button-${item.id}`} onClick={() => approve(item.id)} disabled={item.status === "Approved" || item.status === "Scheduled" || item.status === "Published"}>
                  <Check className="h-4 w-4" /> Approve
                </Btn>
                <Btn variant="outline" testId={`request-change-button-${item.id}`} onClick={() => setRevisionFor(item)}>
                  <PencilLine className="h-4 w-4" /> Request Change
                </Btn>
                <Btn variant="outline" testId={`download-button-${item.id}`} onClick={() => download(item)}>
                  <Download className="h-4 w-4" /> Download
                </Btn>
                <Btn variant="ghost" testId={`copy-caption-button-${item.id}`} onClick={() => copyCaption(item)}>
                  <Copy className="h-4 w-4" /> Copy Caption
                </Btn>
              </div>
            </Card>
          ))}
        </div>
      )}

      {revisionFor && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-slate-900/40 p-4 sm:items-center" role="dialog" aria-label="Request change">
          <div className="w-full max-w-lg rounded-3xl bg-white p-6" data-testid="revision-modal">
            <h2 className="font-display text-xl font-bold text-slate-900">Request a change</h2>
            <p className="mt-1 text-sm text-slate-600">{revisionFor.title} · {revisionFor.post_date}</p>

            <div className="mt-5 space-y-4">
              <Field label="What would you like changed?">
                <CheckboxGroup name="revision-areas" options={changeAreas} value={areas} onChange={setAreas} />
              </Field>
              <Field label="Describe the change" error={error} htmlFor="revision-description">
                <textarea
                  id="revision-description"
                  rows={4}
                  data-testid="revision-description-input"
                  className={inputCls}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Field>
            </div>

            <div className="mt-6 flex flex-wrap gap-2">
              <Btn testId="revision-submit-button" onClick={submitRevision}>Submit Revision</Btn>
              <Btn variant="outline" testId="revision-cancel-button" onClick={() => setRevisionFor(null)}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
